/**
 * Speaker × Format matrix — A2 visual primitive.
 *
 * Renders a (speaker class × format) heat matrix where each cell shows
 * `yes_show%` as the headline number with `events` + `n` as supporting
 * chips. Sparse cells (events < 3, surfaced by the API as
 * `yes_show: null`) render muted with "—".
 *
 * Colour scale: teal-soft → teal → bennett-navy. The scale is anchored
 * to `[0, 1]` (the yes_show value space) rather than the matrix max so
 * the visual is comparable across periods.
 */
import type { SpeakerCell } from '../../data/api/events';
import { formatNumber } from '../../utils/format';

export interface SpeakerMatrixClass {
  id: string;
  label: string;
}

export interface SpeakerMatrixFormat {
  id: string;
  label: string;
}

export interface SpeakerMatrixProps {
  classes: SpeakerMatrixClass[];
  formats: SpeakerMatrixFormat[];
  /** cells[classId][formatId] = cell */
  cells: Record<string, Record<string, SpeakerCell>>;
}

/**
 * Interpolate teal-soft → teal → bennett-navy across [0, 1]. We do the
 * mix in CSS via overlayed semi-transparent layers — that keeps the
 * navy at high yes_show legible while soft cells stay paper-ish.
 *
 * The bottom layer is teal-soft (always visible); on top we paint
 * bennett-navy with alpha = yes_show, giving a smooth ramp.
 */
function cellBg(yesShow: number | null): string | undefined {
  if (yesShow == null) return undefined;
  const a = Math.max(0, Math.min(1, yesShow));
  // 0.06 floor keeps even 0% cells distinguishable from "no data" muted.
  const alpha = 0.06 + a * 0.55;
  return `rgba(43, 40, 107, ${alpha.toFixed(3)})`;
}

function cellTextColor(yesShow: number | null): string {
  if (yesShow == null) return 'var(--ink-3)';
  // Dark navy fill at high yes_show → switch to white for legibility.
  return yesShow >= 0.55 ? 'var(--bennett-white)' : 'var(--ink)';
}

export function SpeakerMatrix({ classes, formats, cells }: SpeakerMatrixProps) {
  // Compute per-row weighted totals so the rightmost summary column is
  // honest — events-weighted mean of yes_show, ignoring null cells.
  const rowSummaries = classes.map((cls) => {
    const row = cells[cls.id] ?? {};
    let wnum = 0;
    let wden = 0;
    let totalEvents = 0;
    let totalN = 0;
    for (const f of formats) {
      const c = row[f.id];
      if (!c) continue;
      totalEvents += c.events;
      totalN += c.n;
      if (c.yes_show != null && c.events > 0) {
        wnum += c.yes_show * c.events;
        wden += c.events;
      }
    }
    return {
      id: cls.id,
      yes_show: wden > 0 ? wnum / wden : null,
      events: totalEvents,
      n: totalN,
    };
  });

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        className="matrix"
        style={{
          width: '100%',
          minWidth: 520,
          borderCollapse: 'separate',
          borderSpacing: 4,
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          <col style={{ width: 200 }} />
          {formats.map((f) => (
            <col key={f.id} />
          ))}
          <col style={{ width: 110 }} />
        </colgroup>
        <thead>
          <tr>
            <th
              style={{
                textAlign: 'right',
                padding: '8px 12px',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                color: 'var(--ink-3)',
                fontWeight: 600,
              }}
            >
              סוג דובר
            </th>
            {formats.map((f) => (
              <th
                key={f.id}
                style={{
                  textAlign: 'center',
                  padding: '8px 4px',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 12,
                  color: 'var(--ink-3)',
                  fontWeight: 600,
                }}
              >
                {f.label}
              </th>
            ))}
            <th
              style={{
                textAlign: 'center',
                padding: '8px 4px',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                color: 'var(--ink-3)',
                fontWeight: 600,
              }}
            >
              סך משוקלל
            </th>
          </tr>
        </thead>
        <tbody>
          {classes.map((cls, i) => {
            const row = cells[cls.id] ?? {};
            const summary = rowSummaries[i]!;
            return (
              <tr key={cls.id}>
                <td
                  style={{
                    padding: '10px 12px',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--ink)',
                    background: 'var(--surface, #fafaf7)',
                    borderRadius: 4,
                  }}
                >
                  {cls.label}
                </td>
                {formats.map((f) => {
                  const c = row[f.id];
                  const insufficient = !c || c.events === 0 || c.yes_show == null;
                  if (insufficient) {
                    return (
                      <td
                        key={f.id}
                        style={{
                          padding: 0,
                          textAlign: 'center',
                          background: 'var(--paper-2, #faf8f3)',
                          borderRadius: 4,
                        }}
                      >
                        <div
                          style={{
                            padding: '14px 6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            color: 'var(--ink-3)',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 11,
                          }}
                          title={
                            c && c.events > 0
                              ? `${c.events} אירועים · נתונים חלקיים (n=${c.n})`
                              : 'אין נתונים בקטגוריה זו'
                          }
                        >
                          <span style={{ fontSize: 14, color: 'var(--ink-4, var(--ink-3))' }}>—</span>
                          {c && c.events > 0 && (
                            <span>
                              n={formatNumber(c.n)} · {c.events} אר׳
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  }
                  return (
                    <td
                      key={f.id}
                      style={{
                        padding: 0,
                        textAlign: 'center',
                        background: cellBg(c.yes_show),
                        borderRadius: 4,
                      }}
                    >
                      <div
                        style={{
                          padding: '14px 6px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2,
                          color: cellTextColor(c.yes_show),
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                        }}
                      >
                        <span style={{ fontSize: 16, fontWeight: 700 }}>
                          {Math.round((c.yes_show ?? 0) * 100)}%
                        </span>
                        <span
                          style={{
                            opacity: 0.85,
                            fontSize: 10.5,
                          }}
                        >
                          ⌀ {formatNumber(Math.round(c.attended_avg ?? 0))} · n={formatNumber(c.n)}
                        </span>
                        <span
                          style={{
                            opacity: 0.7,
                            fontSize: 10,
                          }}
                        >
                          {c.events} אירועים
                        </span>
                      </div>
                    </td>
                  );
                })}
                <td
                  style={{
                    padding: '10px 4px',
                    textAlign: 'center',
                    background: 'var(--surface, #fafaf7)',
                    borderRadius: 4,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 13,
                    fontWeight: 700,
                    color: summary.yes_show == null ? 'var(--ink-3)' : 'var(--ink)',
                  }}
                  title={`${summary.events} אירועים · n=${summary.n}`}
                >
                  {summary.yes_show == null
                    ? '—'
                    : `${Math.round(summary.yes_show * 100)}%`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
